import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Send as SendIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { ACCOUNTING_API, EINVOICE_API } from '../../../config/api';
import { accountingApi, einvoiceApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface EInvoiceHistoryModalProps {
  open: boolean;
  onClose: () => void;
  ettn: string;
  invoiceType: 'GELEN' | 'GIDEN';
  companyId: string;
}

interface HistoryItem {
  bagliKayitId: string;
  faturaEttn: string;
  islem: string;
  islemAciklama: string;
  islemKanali: string;
  islemSonucu: string;
  islemZamani: string;
  islemKanaliAciklama: string;
  kullanici: string;
  kullaniciIp: string;
  zarfEttn: string;
}

const EInvoiceHistoryModal: React.FC<EInvoiceHistoryModalProps> = ({
  open,
  onClose,
  ettn,
  invoiceType,
  companyId,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryItem[]>([]);
  const [eInvoiceIntegration, setEInvoiceIntegration] = useState<any>(null);
  const [isEdm, setIsEdm] = useState(false);

  // Entegrasyon bilgilerini yükle
  useEffect(() => {
    if (open && companyId) {
      loadEInvoiceIntegration();
    }
  }, [open, companyId]);

  // Tarihçe yükle
  useEffect(() => {
    if (open && ettn && companyId && eInvoiceIntegration) {
      fetchHistory();
    }
  }, [open, ettn, companyId, invoiceType, eInvoiceIntegration]);

  const loadEInvoiceIntegration = async () => {
    try {
      console.log('🔐 [E-FATURA HISTORY] Entegrasyon yükleniyor:', companyId);

      const response = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = response.data;
      console.log('✅ [E-FATURA HISTORY] Entegrasyon yüklendi:', result);
      
      // API'den gelen data yapısı: { data: { id, username, password, ... } }
      const integrationData = result.data || result;
      setEInvoiceIntegration(integrationData);
    } catch (err: any) {
      console.error('❌ [E-FATURA HISTORY] Entegrasyon yükleme hatası:', err);
      setError(err.message || tt('Entegrasyon bilgileri yüklenemedi', 'Failed to load integration details'));
    }
  };

  const fetchHistory = async () => {
    if (!eInvoiceIntegration) return;

    setLoading(true);
    setError(null);
    setHistoryData([]);

    try {
      // ✅ EDM entegrasyonu → tarihçe yerine GetInvoiceStatus özeti (EDM'de ayrı journey API yok)
      const providerType = (eInvoiceIntegration.providerType || eInvoiceIntegration.ProviderType || 'finansbank').toString().toLowerCase();
      if (providerType === 'edm') {
        setIsEdm(true);
        await fetchHistoryViaEdm();
        return;
      }
      setIsEdm(false);

      const username = eInvoiceIntegration.username;
      const password = eInvoiceIntegration.password;
      const taxNumber = eInvoiceIntegration.taxNumber || eInvoiceIntegration.username;

      console.log('🔍 [E-FATURA HISTORY] Tarihçe sorgulanıyor...', {
        ettn,
        invoiceType,
        username,
        taxNumber,
      });

      // Backend parametreleri query string'den bekliyor, [FromBody] yok
      const params = new URLSearchParams({
        username: username,
        password: password,
        vkn: taxNumber,
        ettn: ettn || '',
        faturaYonu: invoiceType,
      });
      
      const endpoint = `${EINVOICE_API.QNB_FATURA_TARIHCE_SORGULA}?${params.toString()}`;

      console.log('📤 [E-FATURA HISTORY] API isteği:', endpoint.replace(password, '***'));

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      console.log('📡 [E-FATURA HISTORY] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [E-FATURA HISTORY] API hatası:', errorText);
        throw new Error(errorText || tt('Tarihçe sorgulanamadı', 'History query failed'));
      }

      const data = await response.json();
      console.log('📄 [E-FATURA HISTORY] Response data:', data);

      // Backend'den camelCase olarak geliyor: tarihce (not Tarihce)
      const tarihceData = data.tarihce || data.Tarihce;
      
      if (tarihceData && tarihceData.length > 0) {
        setHistoryData(tarihceData);
        console.log('✅ [E-FATURA HISTORY] Tarihçe yüklendi:', tarihceData.length, 'kayıt');
      } else {
        setHistoryData([]);
        console.log('⚠️ [E-FATURA HISTORY] Tarihçe verisi bulunamadı');
      }
    } catch (err: any) {
      console.error('❌ [E-FATURA HISTORY] Hata:', err);
      setError(err.message || tt('Fatura tarihçesi sorgulanırken bir hata oluştu', 'An error occurred while querying invoice history'));
    } finally {
      setLoading(false);
    }
  };

  // ✅ EDM: tarihçe API'si yok → GetInvoiceStatus sonucundan özet akış üret
  const fetchHistoryViaEdm = async () => {
    try {
      const resp = await einvoiceApi.post(EINVOICE_API.EDM_INBOX_STATUS, {
        uuid: ettn || null,
        id: null,
        branchId: null,
      });
      const data = resp.data;

      if (!data || data.success === false) {
        throw new Error(data?.error || tt('Durum sorgulanamadı', 'Status query failed'));
      }
      if (!data.found) {
        setHistoryData([]);
        return;
      }

      const steps: HistoryItem[] = [];

      // GİB / işlenme durumu
      if (data.gibStatusDescription || data.statusDescription) {
        steps.push({
          bagliKayitId: 'edm-status',
          faturaEttn: data.uuid || ettn,
          islem: tt('GİB Durumu', 'Tax Authority Status'),
          islemAciklama: data.gibStatusDescription || data.statusDescription || '',
          islemKanali: 'EDM',
          islemKanaliAciklama: 'EDM',
          islemSonucu: data.statusDescription || tt('Bilgi', 'Info'),
          islemZamani: '',
          kullanici: '',
          kullaniciIp: '',
          zarfEttn: data.envelopeIdentifier || '',
        });
      }

      // Yanıt (KABUL/RED) durumu
      if (data.responseDescription || data.responseCode) {
        const respText = `${data.responseDescription || ''} ${data.responseCode || ''}`.toLocaleUpperCase('tr-TR');
        const sonuc = respText.includes('KABUL')
          ? tt('Kabul Edildi', 'Accepted')
          : respText.includes('RED')
          ? tt('Reddedildi', 'Rejected')
          : (data.responseDescription || tt('Yanıtlandı', 'Responded'));
        steps.push({
          bagliKayitId: 'edm-response',
          faturaEttn: data.uuid || ettn,
          islem: tt('Fatura Yanıtı', 'Invoice Response'),
          islemAciklama: data.responseDescription || '',
          islemKanali: 'EDM',
          islemKanaliAciklama: 'EDM',
          islemSonucu: sonuc,
          islemZamani: '',
          kullanici: '',
          kullaniciIp: '',
          zarfEttn: '',
        });
      }

      setHistoryData(steps);
    } catch (err: any) {
      const apiErr = err?.response?.data?.error || err?.response?.data?.errorLong;
      throw new Error(apiErr || err.message || tt('Tarihçe sorgulanamadı', 'History query failed'));
    }
  };

  const formatDateTime = (dateTimeStr: string): string => {
    // Format: 20251231123045123 -> 31.12.2025 12:30:45
    if (!dateTimeStr || dateTimeStr.length < 14) return dateTimeStr;

    const year = dateTimeStr.substring(0, 4);
    const month = dateTimeStr.substring(4, 6);
    const day = dateTimeStr.substring(6, 8);
    const hour = dateTimeStr.substring(8, 10);
    const minute = dateTimeStr.substring(10, 12);
    const second = dateTimeStr.substring(12, 14);

    return `${day}.${month}.${year} ${hour}:${minute}:${second}`;
  };

  const getStatusIcon = (islemSonucu: string) => {
    if (islemSonucu?.toLowerCase().includes('başarılı') || islemSonucu?.toLowerCase().includes('kabul')) {
      return <CheckCircleIcon />;
    } else if (islemSonucu?.toLowerCase().includes('hata') || islemSonucu?.toLowerCase().includes('red')) {
      return <ErrorIcon />;
    } else if (islemSonucu?.toLowerCase().includes('gönder')) {
      return <SendIcon />;
    } else if (islemSonucu?.toLowerCase().includes('fatura')) {
      return <ReceiptIcon />;
    } else {
      return <InfoIcon />;
    }
  };

  const getStatusColor = (islemSonucu: string): string => {
    if (islemSonucu?.toLowerCase().includes('başarılı') || islemSonucu?.toLowerCase().includes('kabul')) {
      return '#4caf50';
    } else if (islemSonucu?.toLowerCase().includes('hata') || islemSonucu?.toLowerCase().includes('red')) {
      return '#f44336';
    } else if (islemSonucu?.toLowerCase().includes('bekle')) {
      return '#ff9800';
    } else if (islemSonucu?.toLowerCase().includes('bilgi') || islemSonucu?.toLowerCase().includes('işlen')) {
      return '#2196f3';
    } else {
      return '#9e9e9e';
    }
  };

  const getChipColor = (islemSonucu: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    if (islemSonucu?.toLowerCase().includes('başarılı') || islemSonucu?.toLowerCase().includes('kabul')) {
      return 'success';
    } else if (islemSonucu?.toLowerCase().includes('hata') || islemSonucu?.toLowerCase().includes('red')) {
      return 'error';
    } else if (islemSonucu?.toLowerCase().includes('bekle')) {
      return 'warning';
    } else if (islemSonucu?.toLowerCase().includes('bilgi') || islemSonucu?.toLowerCase().includes('işlen')) {
      return 'info';
    } else {
      return 'default';
    }
  };

  const handleClose = () => {
    setError(null);
    setHistoryData([]);
    setIsEdm(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
        <HistoryIcon />
        {invoiceType === 'GELEN' ? tt('📥 Gelen', '📥 Incoming') : tt('📤 Giden', '📤 Outgoing')} {tt('E-Fatura Tarihçesi', 'E-Invoice History')}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* ETTN Bilgisi */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            📋 ETTN
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
            {ettn}
          </Typography>
          <Chip 
            label={invoiceType === 'GELEN' ? tt('Gelen Fatura', 'Incoming Invoice') : tt('Giden Fatura', 'Outgoing Invoice')} 
            size="small" 
            color={invoiceType === 'GELEN' ? 'primary' : 'secondary'}
            sx={{ mt: 1 }}
          />
        </Paper>

        {/* EDM bilgilendirme notu */}
        {isEdm && !loading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {tt(
              'EDM entegrasyonunda ayrıntılı işlem tarihçesi bulunmaz; mevcut GİB/yanıt durumu özeti gösterilmektedir.',
              'EDM integration does not provide a detailed action history; the current tax authority/response status summary is shown.'
            )}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {tt('Fatura tarihçesi yükleniyor...', 'Loading invoice history...')}
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ❌ {tt('Hata', 'Error')}
            </Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {/* History Stepper */}
        {!loading && !error && historyData.length > 0 && (
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto', mt: 2 }}>
            <Stepper orientation="vertical" activeStep={historyData.length}>
              {historyData.map((item, index) => (
                <Step key={item.bagliKayitId || index} active={true} completed={true}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Avatar
                        sx={{
                          bgcolor: getStatusColor(item.islemSonucu),
                          width: 40,
                          height: 40,
                        }}
                      >
                        {getStatusIcon(item.islemSonucu)}
                      </Avatar>
                    )}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {item.islem}
                      </Typography>
                      {item.islemSonucu && (
                        <Chip 
                          label={item.islemSonucu}
                          size="small"
                          color={getChipColor(item.islemSonucu)}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(item.islemZamani)}
                      </Typography>
                      {item.kullanici && (
                        <>
                          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                          <Typography variant="caption" color="text.disabled">
                            👤 {item.kullanici}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        p: 2, 
                        mt: 1, 
                        mb: 2, 
                        bgcolor: 'grey.50',
                        borderLeft: `4px solid ${getStatusColor(item.islemSonucu)}`,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {item.islemAciklama}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        {item.islemKanali && (
                          <Chip 
                            label={`📡 ${item.islemKanaliAciklama || item.islemKanali}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        
                        {item.kullaniciIp && (
                          <Chip 
                            label={`🌐 ${item.kullaniciIp}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Paper>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        {/* Empty State */}
        {!loading && !error && historyData.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HistoryIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {tt('Tarihçe Bulunamadı', 'History Not Found')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {tt('Bu fatura için henüz tarihçe kaydı bulunmamaktadır.', 'No history records were found for this invoice yet.')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined">
          {tt('Kapat', 'Close')}
        </Button>
        {!loading && historyData.length > 0 && (
          <Button onClick={fetchHistory} variant="contained" startIcon={<HistoryIcon />}>
            {tt('Yenile', 'Refresh')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EInvoiceHistoryModal;
